import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Free gifts for cart goals (Theme settings > Cart > Cart goals).
 *
 * After every cart change (and once on page load) this finds the single highest goal the cart
 * total has reached (goals without a gift, like free shipping, count too). Only one offer
 * applies at a time:
 *   - that goal has a gift and it's missing → add 1 of it
 *   - any other goal's gift in the cart → remove it
 *   - gift quantity changed → reset to 1
 *
 * Gift lines carry the hidden line property `_cart_goal_gift` ("goal-1", "goal-2"...), so
 * only gifts added here are touched, and they don't count towards the goal totals.
 * This only manages the cart lines; an automatic discount in Shopify makes the gift free.
 *
 * Config comes from <script type="application/json" id="cart-goal-gifts-config">
 * rendered by snippets/cart-drawer.liquid.
 */

const GIFT_PROPERTY = '_cart_goal_gift';
const SOURCE = 'cart-goal-gifts';

/** @type {{ goals: { id: string, threshold: number, variantId: number | null }[] } | null} */
const config = readConfig();

let running = false;
let queued = false;

if (config?.goals.length) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {CartLinesUpdateEvent} */ (event).promise
      ?.then(({ detail }) => {
        if (detail?.source !== SOURCE) sync();
      })
      .catch(() => {});
  });

  sync();
}

function readConfig() {
  const element = document.getElementById('cart-goal-gifts-config');
  if (!element) return null;
  try {
    return JSON.parse(element.textContent || '');
  } catch {
    return null;
  }
}

/** Runs one sync at a time; a change during a sync triggers one more pass afterwards. */
async function sync() {
  if (running) {
    queued = true;
    return;
  }
  running = true;
  try {
    await applyChanges(await fetchCart());
  } catch (error) {
    console.warn('[cart-goal-gifts]', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      sync();
    }
  }
}

async function fetchCart() {
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/** @param {any} cart */
async function applyChanges(cart) {
  if (!config) return;

  const giftLines = cart.items.filter((item) => item.properties?.[GIFT_PROPERTY]);
  const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
  const qualifyingTotal = cart.total_price - giftTotal;

  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {{ id: number, quantity: number, properties: Record<string, string> }[]} */
  const additions = [];

  // One offer at a time: only the highest goal reached applies. If that goal has no gift
  // (e.g. free shipping), no gift is in the cart; lower goals' gifts are removed.
  const activeGoal = config.goals
    .filter((goal) => qualifyingTotal >= goal.threshold)
    .reduce((best, goal) => (!best || goal.threshold > best.threshold ? goal : best), null);

  for (const goal of config.goals) {
    const reached = goal === activeGoal && goal.variantId != null;
    const lines = giftLines.filter((item) => item.properties[GIFT_PROPERTY] === goal.id);

    if (reached) {
      if (lines.length === 0) {
        additions.push({ id: goal.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: goal.id } });
      } else {
        lines.forEach((line, index) => {
          const quantity = index === 0 ? 1 : 0;
          if (line.quantity !== quantity) updates[line.key] = quantity;
        });
      }
    } else {
      lines.forEach((line) => {
        updates[line.key] = 0;
      });
    }
  }

  // Gifts for goals that were removed from the settings
  const goalIds = new Set(config.goals.map((goal) => goal.id));
  giftLines.forEach((line) => {
    if (!goalIds.has(line.properties[GIFT_PROPERTY])) updates[line.key] = 0;
  });

  if (Object.keys(updates).length === 0 && additions.length === 0) return;

  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: additions.length ? 'add' : Object.values(updates).every((quantity) => quantity === 0) ? 'remove' : 'update',
      context: 'cart',
      lines: additions.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    let sectionsHtml;

    if (Object.keys(updates).length) {
      const result = await postJson(`${routes().cart_update_url ?? '/cart/update'}.js`, { updates, sections });
      sectionsHtml = result.sections;
    }

    if (additions.length) {
      const result = await postJson(routes().cart_add_url ?? '/cart/add.js', { items: additions, sections });
      if (result.status) throw new Error(result.description || result.message || 'Could not add gift');
      sectionsHtml = result.sections;
    }

    const updatedCart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
      detail: {
        items: updatedCart.items,
        source: SOURCE,
        sections: sectionsHtml,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * @param {string} url
 * @param {object} body
 */
async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

function routes() {
  return window.Theme?.routes ?? {};
}

function cartUrl() {
  return routes().cart_url ?? '/cart';
}
